import { useState } from 'react'

import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { type POPDFCustomFieldDisplayRow } from '@webapp/modules/purchasing/business-logic/use-cases/getPOPDFCustomFieldDisplay'
import { useTogglePOPDFCustomFieldDisplayPresenter } from '@webapp/modules/purchasing/features/toggle-po-pdf-custom-field-display/presenters/UseTogglePOPDFCustomFieldDisplayPresenter'
// eslint-disable-next-line import/no-internal-modules
import { type POPDFHiddenCustomField } from '@webapp/platform/iam/entities/SystemPreference'

/**
 * Orchestration hook for POPDFCustomFieldDisplayView.
 *
 * The save contract is the WHOLE hidden map, not per-row — so the Feature Set
 * rebuilds the full `POPDFHiddenCustomField[]` from both tables' current rows
 * with the toggled entry patched, applies a transient optimistic flip, and
 * reverts it when the command rejects (BHV-045/046). The error toast comes
 * from the command hook (ADR-10).
 */
export const usePOPDFCustomFieldDisplayView = () => {
  // Transient optimistic flips (field id → displayInPDF), applied by the
  // list presenter over its query rows
  const [displayOverrides, setDisplayOverrides] = useState<
    Record<number, boolean>
  >({})

  const {
    commands: { toggle },
    states: { isSaving },
  } = useTogglePOPDFCustomFieldDisplayPresenter()

  const handleToggle = async (
    field: CustomField,
    hidden: boolean,
    currentRows: {
      po: POPDFCustomFieldDisplayRow[]
      orderItem: POPDFCustomFieldDisplayRow[]
    }
  ) => {
    const hiddenMap: POPDFHiddenCustomField[] = [
      ...currentRows.po,
      ...currentRows.orderItem,
    ].map((row) => ({
      customField: row.field.id,
      hidden: row.field.id === field.id ? hidden : !row.displayInPDF,
    }))

    setDisplayOverrides((overrides) => ({ ...overrides, [field.id]: !hidden }))

    try {
      // Resolves only after the display query has reconciled (the command
      // Model awaits its invalidations), so dropping the override below
      // never flashes stale data
      await toggle(hiddenMap)
    } catch {
      // Dropping the override below reverts the optimistic flip (BHV-046);
      // the error toast is owned by the command hook
    } finally {
      // Toggles are disabled while a save is in flight, so at most one
      // override exists — reset the map
      setDisplayOverrides({})
    }
  }

  return {
    states: {
      displayOverrides,
      isSaving,
    },
    methods: {
      handleToggle,
    },
  }
}
